// UC30 — add / edit a storage site. Archetype D.
// The code is the MPass organisation code composed as IDNO + a four-digit branch
// suffix (UC30-04, ruling D4), the locality is mandatory (UC30-05), and the
// System Administrator must name the Registrar he creates for (UC21-09).
// per docs/shape-uc-30-sites.md §2, §3
// per docs/shape-uc-30-sites.md §9 (addressed by the code, every type, the block tags)
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslate } from '@tolgee/react';
import { MudTextInput, MudSelect, MudSwitch } from '@egov-moldova/mud-react';
import {
  type ApiRefusal,
  type LoadFailureKind,
  apiClient,
  apiRefusal,
  loadFailureKind,
} from '../../app/api-client';
import { FormField, FormPage, FormSection } from '../../components/FormPage/FormPage';
import { useSession } from '../../hooks/use-session';
import { useActiveRegistrar } from '../../hooks/use-active-registrar';
import { ActiveRegistrarNote } from '../../components/RegistrarSelector/RegistrarSelector';
import { listRegistrars } from '../../features/registrars/api';
import { blockViewerOf, otherBlocked, ownBlocked } from '../../features/blocking/block-status';
import { BlockTags, SITE_BLOCK_LABELS } from '../../features/blocking/BlockTags';
import { SearchSelect } from './SearchSelect';
import { BRANCH_SUFFIX_LENGTH, readSuffixInput, suffixProblem } from './site-code';
import {
  fetchLocalities,
  fetchSites,
  pickableLocalities,
  SITE_TIPS,
  type SiteRow,
} from './sites-api';

interface FormState {
  registrarId: string;
  name: string;
  suffix: string;
  tip: string;
  adresa: string;
  geographyId: string;
  active: boolean;
}

function emptyForm(registrarId: string): FormState {
  return {
    registrarId,
    name: '',
    suffix: '',
    tip: 'farmacie',
    adresa: '',
    geographyId: '',
    active: true,
  };
}

function comparable(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '');
}

export function SiteFormPage(): JSX.Element {
  const { t } = useTranslate();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  // The site is addressed by its location code (ACCS-12): the MPass code the API
  // also names it by. per docs/shape-ops-readable-urls-and-history.md §1
  const { code: routeCode, role = 'registrar-admin' } = useParams<{
    code: string;
    role: string;
  }>();
  const isEdit = Boolean(routeCode);
  const session = useSession();
  const isSystemAdmin = session.data?.activeRole === 'SYSTEM_ADMIN';
  // CF-48: the switch is the viewer's own block; the other level's is named
  // under it and never sent from here.
  const viewer = blockViewerOf(session.data?.activeRole);
  const activeRegistrar = useActiveRegistrar();
  // The Registrar a NEW site is created under. Owner ruling 2026-09-06
  // (finding D-14): the one being worked on, not the first on the session.
  const ownRegistrar = activeRegistrar.idno;

  const [params] = useSearchParams();
  const scopedRegistrar = params.get('registrar') ?? '';
  const backTo = `/${role}/sites${params.toString() ? `?${params.toString()}` : ''}`;

  const [form, setForm] = useState<FormState>(() =>
    emptyForm(isSystemAdmin ? scopedRegistrar : ownRegistrar),
  );
  const [initial, setInitial] = useState<FormState>(form);
  const [submitError, setSubmitError] = useState<ApiRefusal | null>(null);
  const [submitted, setSubmitted] = useState(false);

  const localities = useQuery({
    queryKey: ['admin-site-localities'],
    queryFn: fetchLocalities,
    staleTime: 5 * 60_000,
  });
  const registrars = useQuery({
    queryKey: ['admin-registrars', ''],
    queryFn: () => listRegistrars({}),
    enabled: isSystemAdmin,
    staleTime: 5 * 60_000,
  });

  // UC30-03 is a rule about one Registrar's sites, so the check needs them.
  // The same read loads the row being edited: the list is the only place the API
  // exposes a site. Without a Registrar in the address, a System Administrator
  // reads the cumulated list, where the code still names one row (CF-08).
  const siblingsQuery = useQuery({
    queryKey: ['admin-sites', form.registrarId || scopedRegistrar],
    queryFn: () => fetchSites(form.registrarId || scopedRegistrar),
    enabled: !isEdit ? Boolean(form.registrarId) : true,
  });
  const siblings = useMemo(() => siblingsQuery.data ?? [], [siblingsQuery.data]);
  // CF-08: the code names one site across the system. `?registrar=` (a System
  // Administrator's link) or the active Registrar scopes the read; a code the
  // read still holds twice opens neither, never a guess.
  const edited: SiteRow | undefined = useMemo(() => {
    if (!isEdit) return undefined;
    const matches = siblings.filter((site) => site.internalCode === routeCode);
    return matches.length === 1 ? matches[0] : undefined;
  }, [isEdit, routeCode, siblings]);

  useEffect(() => {
    if (!isEdit || !edited) return;
    const next: FormState = {
      registrarId: edited.registrarId,
      name: edited.name,
      suffix: '',
      tip: edited.tip,
      adresa: edited.adresa,
      geographyId: edited.geographyId,
      active: !ownBlocked(edited, viewer),
    };
    setForm(next);
    setInitial(next);
  }, [isEdit, edited, viewer]);

  const registrar = registrars.data?.find((entity) => entity.idno === form.registrarId);
  const registrarIdno = isSystemAdmin ? (registrar?.idno ?? form.registrarId) : ownRegistrar;
  const composedCode = registrarIdno ? `${registrarIdno}${form.suffix}` : '';
  const code = isEdit ? (edited?.internalCode ?? '') : composedCode;

  const pickable = useMemo(
    () => pickableLocalities(localities.data ?? [], edited?.geographyId),
    [localities.data, edited?.geographyId],
  );
  const localityOptions = useMemo(
    () =>
      pickable.map((locality) => ({
        value: locality.id,
        label: locality.name,
        ...(locality.area ? { meta: locality.area } : {}),
      })),
    [pickable],
  );

  const nameClash = siblings.find(
    (site) =>
      site.id !== edited?.id &&
      form.name.trim().length > 0 &&
      comparable(site.name) === comparable(form.name),
  );
  const codeClash =
    !isEdit && form.suffix.length === BRANCH_SUFFIX_LENGTH
      ? siblings.find((site) => site.internalCode === composedCode)
      : undefined;

  const registrarError = !form.registrarId ? t('sites.error.registrar_required') : '';
  const nameError = !form.name.trim()
    ? t('sites.error.name_required')
    : nameClash
      ? t('sites.error.name_taken', { registrar: registrar?.name ?? '' })
      : '';
  const problem = isEdit ? null : suffixProblem(form.suffix, registrarIdno);
  const codeError = isEdit
    ? ''
    : problem === 'required'
      ? t('sites.error.suffix_required')
      : problem === 'full_code_prefix'
        ? t('sites.error.suffix_prefix', { idno: registrarIdno })
        : problem === 'length'
          ? t('sites.error.suffix_length')
          : codeClash
            ? t('sites.error.code_taken', { code: composedCode })
            : '';
  const localityError = !form.geographyId ? t('sites.error.locality_required') : '';
  const isValid = !registrarError && !nameError && !codeError && !localityError;

  const writeParams = isSystemAdmin ? { registrarId: form.registrarId } : {};

  const create = useMutation({
    mutationFn: () =>
      apiClient
        .post<SiteRow>(
          '/admin/sites',
          {
            internalCode: composedCode,
            name: form.name.trim(),
            tip: form.tip,
            adresa: form.adresa,
            geographyId: form.geographyId,
            active: form.active,
          },
          { params: writeParams },
        )
        .then((response) => response.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-sites'] });
      navigate(backTo);
    },
    // The server owns UC30-03: a name or a code can be taken between the moment
    // the form checked and the moment it saves, and only the server sees that.
    // Creating and updating are both saves, so both fall back to the same key.
    onError: (error) => setSubmitError(apiRefusal(error, 'form.error.save_failed')),
  });

  const update = useMutation({
    // The route key is the site's MPass code, not its UUID: the address and the
    // API name the site by the same code.
    mutationFn: () =>
      apiClient
        .patch<SiteRow>(
          `/admin/sites/${encodeURIComponent(code)}`,
          {
            name: form.name.trim(),
            tip: form.tip,
            adresa: form.adresa,
            geographyId: form.geographyId,
            // Only a switch the viewer moved: re-sending the state would set or
            // refuse a block the save was not about.
            ...(form.active !== initial.active ? { active: form.active } : {}),
          },
          { params: writeParams },
        )
        .then((response) => response.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-sites'] });
      navigate(backTo);
    },
    onError: (error) => setSubmitError(apiRefusal(error, 'form.error.save_failed')),
  });

  // The storage site is read out of its Registrar's list, so absence is only
  // knowable from a list that arrived: a failed read used to reach the same
  // branch as a genuinely missing row and announce that the site did not exist.
  const editLoadError: LoadFailureKind | null = !isEdit
    ? null
    : siblingsQuery.isError
      ? loadFailureKind(siblingsQuery.error)
      : siblingsQuery.isSuccess && !edited
        ? 'not_found'
        : null;

  function handleSave(): void {
    setSubmitError(null);
    setSubmitted(true);
    if (!isValid) return;
    if (isEdit) update.mutate();
    else create.mutate();
  }

  return (
    <FormPage
      breadcrumb={[
        { label: t('nav.sites'), href: backTo },
        { label: isEdit ? t('sites.edit.title') : t('sites.new.title') },
      ]}
      title={isEdit ? t('sites.edit.title') : t('sites.new.title')}
      lede={isEdit ? t('sites.edit.lede') : t('sites.new.lede')}
      saveLabel={isEdit ? t('sites.action.save') : t('sites.action.create')}
      isSaving={create.isPending || update.isPending}
      isDirty={JSON.stringify(form) !== JSON.stringify(initial)}
      isLoading={isEdit && siblingsQuery.isLoading}
      loadError={editLoadError}
      refusal={submitError}
      canSave={isValid}
      onSave={handleSave}
      onCancel={() => navigate(backTo)}
    >
      {isSystemAdmin ? (
        <FormSection title={t('sites.section.ownership')}>
          <FormField
            label={t('sites.column.registrar')}
            fullWidth
            required
            hint={t('sites.field.registrar_hint')}
            error={submitted && registrarError ? registrarError : undefined}
          >
            {isEdit ? (
              <MudTextInput size="md" readonly value={registrar?.name ?? form.registrarId} />
            ) : (
              <SearchSelect
                value={form.registrarId}
                options={(registrars.data ?? [])
                  .filter((entity) => entity.active)
                  .map((entity) => ({
                    value: entity.idno,
                    label: entity.name,
                    meta: `IDNO ${entity.idno}`,
                  }))}
                placeholder={t('sites.field.registrar_placeholder')}
                ariaLabel={t('sites.column.registrar')}
                optionsUnavailable={registrars.isError}
                invalid={submitted && Boolean(registrarError)}
                onPick={(value) => setForm({ ...form, registrarId: value })}
              />
            )}
          </FormField>
        </FormSection>
      ) : null}

      {/* A System Administrator names the owning Registrar in the section
          above. An administrator holding two of them never did — the site was
          filed against `registrarScope[0]` with nothing on the screen saying
          so (owner ruling 2026-09-06, finding D-14). The section is drawn only
          when there is genuinely a second entity the site could have gone to. */}
      {!isSystemAdmin && !isEdit && activeRegistrar.hasChoice ? (
        <FormSection title={t('sites.section.ownership')}>
          <ActiveRegistrarNote />
        </FormSection>
      ) : null}

      <FormSection title={t('sites.section.identity')}>
        <FormField
          label={t('sites.field.name')}
          fullWidth
          required
          hint={t('sites.field.name_hint')}
          error={submitted && nameError ? nameError : undefined}
        >
          <MudTextInput
            size="md"
            value={form.name}
            placeholder={t('sites.field.name_placeholder')}
            invalid={submitted && Boolean(nameError)}
            onMudInput={(event: CustomEvent<{ value: string }>) =>
              setForm({ ...form, name: event.detail.value })
            }
          />
        </FormField>

        <FormField
          label={t('sites.field.code')}
          fullWidth
          required
          hint={
            isEdit
              ? t('sites.field.code_locked')
              : form.suffix.length === BRANCH_SUFFIX_LENGTH && registrarIdno
                ? t('sites.field.code_composed', { code: composedCode })
                : t('sites.field.code_hint')
          }
          error={submitted && codeError ? codeError : undefined}
        >
          {isEdit ? (
            <MudTextInput size="md" readonly value={code} />
          ) : (
            <div className="simsm-sites__code">
              <MudTextInput
                size="md"
                readonly
                value={registrarIdno}
                aria-label={t('sites.field.code_prefix')}
                placeholder={t('sites.field.code_prefix')}
              />
              <span className="simsm-sites__code-join" aria-hidden="true">
                +
              </span>
              <MudTextInput
                size="md"
                value={form.suffix}
                inputmode="numeric"
                placeholder="0001"
                aria-label={t('sites.field.code_suffix')}
                invalid={submitted && Boolean(codeError)}
                {...(registrarIdno ? {} : { disabled: true })}
                onMudInput={(event: CustomEvent<{ value: string }>) =>
                  setForm({ ...form, suffix: readSuffixInput(event.detail.value, registrarIdno) })
                }
              />
            </div>
          )}
        </FormField>

        <FormField label={t('sites.modal.field.tip')} fullWidth>
          <MudSelect
            size="medium"
            value={form.tip}
            onMudChange={(event: CustomEvent<{ value: string }>) =>
              setForm({ ...form, tip: event.detail.value })
            }
          >
            {SITE_TIPS.map((tip) => (
              <option key={tip} value={tip}>
                {t(`sites.tip.${tip}`)}
              </option>
            ))}
          </MudSelect>
        </FormField>
      </FormSection>

      <FormSection title={t('sites.section.location')}>
        <FormField
          label={t('sites.column.locality')}
          fullWidth
          required
          hint={t('sites.field.locality_hint')}
          error={submitted && localityError ? localityError : undefined}
        >
          <SearchSelect
            value={form.geographyId}
            options={localityOptions}
            placeholder={t('sites.field.locality_placeholder')}
            ariaLabel={t('sites.column.locality')}
            optionsUnavailable={localities.isError}
            invalid={submitted && Boolean(localityError)}
            onPick={(value) => setForm({ ...form, geographyId: value })}
          />
        </FormField>
        <FormField label={t('sites.modal.field.adresa')} fullWidth>
          <MudTextInput
            size="md"
            value={form.adresa}
            onMudInput={(event: CustomEvent<{ value: string }>) =>
              setForm({ ...form, adresa: event.detail.value })
            }
          />
        </FormField>
      </FormSection>

      <FormSection title={t('sites.section.state')}>
        {/* CF-48: the state as the list's Statut column draws it, so a block of
            the other level is named here too, above the viewer's own switch. */}
        {edited ? (
          <FormField label={t('sites.column.status')} fullWidth>
            <span className="simsm-sites__state">
              <BlockTags item={edited} viewer={viewer} labels={SITE_BLOCK_LABELS} />
            </span>
          </FormField>
        ) : null}
        <FormField
          label={t('sites.field.active')}
          fullWidth
          hint={
            edited && otherBlocked(edited, viewer)
              ? t(
                  viewer === 'SYSTEM_ADMIN'
                    ? 'sites.form.blocked_by_registrar'
                    : 'sites.form.blocked_by_system_admin',
                )
              : t('sites.field.active_hint')
          }
        >
          <MudSwitch
            checked={form.active}
            label={t('sites.field.active')}
            onMudChange={(event: CustomEvent<{ checked: boolean }>) =>
              setForm({ ...form, active: event.detail.checked })
            }
          />
        </FormField>
      </FormSection>
    </FormPage>
  );
}
